import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { LiveSummary, Platform } from '@multimod/shared';
import { detachLive, type DetachLiveInput } from '@/lib/api';

interface UseDetachLiveOpts {
  // Chamado em `onMutate`: caller remove a plataforma do array local e retorna a
  // live removida (ou null) pra eventual revert.
  onOptimisticRemove: (platform: Platform) => LiveSummary | null;
  // Chamado em `onError`: caller re-adiciona a live que ESTA mutation removeu.
  onRevert: (platform: Platform, removed: LiveSummary | null) => void;
}

interface DetachContext {
  removed: LiveSummary | null;
}

/**
 * Mutation pra DELETE /chat/attach (#131).
 *
 * Optimistic UI: caller remove a live anexada do array local em `onOptimisticRemove`
 * e re-adiciona em `onRevert` se a request falhar. `onSuccess` invalida
 * `['me','streams']` pra refetchar a lista de lives ativas (cobertura de race
 * com refetch periódico).
 *
 * #156: o snapshot da live removida viaja no **context** da mutation (não num ref
 * compartilhado no caller). Assim dois detaches concorrentes (Twitch + YouTube)
 * não sobrescrevem o snapshot um do outro — cada `onError` reverte exatamente a
 * live que a sua mutation tirou.
 *
 * Toast só em erro (convenção do projeto — memory feedback_no_success_toasts).
 * 404 vindo do API é tratado como sucesso (idempotente).
 */
export function useDetachLive(opts: UseDetachLiveOpts) {
  const qc = useQueryClient();
  return useMutation<void, Error, DetachLiveInput, DetachContext>({
    mutationFn: (input: DetachLiveInput) => detachLive(input),
    onMutate: ({ detachPlatform }) => {
      const removed = opts.onOptimisticRemove(detachPlatform);
      return { removed };
    },
    onError: (_err, { detachPlatform }, context) => {
      opts.onRevert(detachPlatform, context?.removed ?? null);
      toast.error('Não foi possível desanexar a live. Tente novamente.');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'streams'] });
    },
  });
}
